import { batch } from 'react-redux';
import difference from 'lodash/difference';

import { doRequest } from '@r1-frontend/do-request';
import { getInfoAll as getInfoAllRequest, TModelsTypes } from '@r1-frontend/api-domru/api-profile/v2/info/all';
import { ResponseContactDto } from '@r1-frontend/api-domru/api-profile/v2/info/all/dto/ResponseDto';

import getAccessToken from '@r1-frontend/shared/helpers/getAccessToken';
import { getAgreementNumber } from '@r1-frontend/shared/helpers/getAgreementNumber';

import { USER_LOGOUT_ROUTE } from '~/src/constants/routes';
import { TAppThunkDispatch, TGetState } from '~/src/store';
import {
    clientContactsLoading,
    clientProductsLoading,
    setClientContacts,
    setClientInfoStage,
    setClientPayment,
    setClientPersonal,
    setClientProducts,
    setFetchClientPayment,
    setLogin,
    TSetClientInfoStage,
} from '~/src/store/actionCreators/auth';
import { EStage, IClientContacts, IClientPersonal } from '~/src/store/reducers/auth';
import { selectHasAuthorization } from '~/src/store/selectors/auth/selectAccessToken';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const getStageForModels = (stageForUpdate: EStage, updatedModels: TModelsTypes[]): TSetClientInfoStage => {
    const stages = {} as TSetClientInfoStage;
    updatedModels.forEach(model => stages[model] = stageForUpdate);
    return stages;
};

export const parseContacts = (_contacts: ResponseContactDto['contacts']): IClientContacts => {
    const { contacts } = _contacts;
    return {
        email: contacts.email.map((el) => ({
            ...el,
            value: el.contactValue,
        })),
        phone: contacts.phone.map((el) => ({
            ...el,
            value: el.contactValue,
        })),
    };
};

// для остановки повторных запросов поверх асинхронного редакса,
// тк не успевает отрабатывать при нескольких параллельных вызовах на странице
const currentlyRequestedModels = new Set();

interface IOldFlags {
    clientPaymentFetching: boolean, // заменит clientInfoStage
    clientPaymentLoaded: number | null, // заменит clientInfoStage
    isClientProductsLoading: boolean, // заменит clientInfoStage
    isClientContactsLoading: boolean, // заменит clientInfoStage
    clientPersonal: IClientPersonal | null,
}

const modelUpdatedByOldFlags = (updatedModels: TModelsTypes[], oldFlags: IOldFlags, isForceUpdate?: boolean) => {
    let result = updatedModels;

    // Не выполняем запрос если данные загружаются по старым флагам
    if (oldFlags.clientPaymentFetching) {
        result = []; // старый флаг ставится на запрос всех моделей 'personal', 'payment', 'products', 'contacts'
    }
    if (oldFlags.isClientProductsLoading) {
        result = result.filter(model => model !== 'products'); // старый флаг ставится на запрос модели 'products'
    }
    if (oldFlags.isClientContactsLoading) {
        result = result.filter(model => model !== 'contacts'); // старый флаг ставится на запрос модели 'contacts'
    }

    // Не выполняем запрос если данные загружены по старым флагам
    if (!isForceUpdate) {
        if (oldFlags.clientPaymentLoaded) {
            result = result.filter(model => model !== 'payment'); // старый флаг ставится на запрос модели 'payment'
        }
        if (oldFlags.clientPersonal) {
            result = result.filter(model => model !== 'personal'); // старый флаг ставится на запрос модели 'personal'
        }
    }
    return result;
};

interface IStateData extends IOldFlags {
    clientInfoStage: {
        [key in TModelsTypes]: EStage;
    },
}

const getFilteredModels = (models: TModelsTypes[], stateData: IStateData, isForceUpdate?: boolean) => {
    const { clientInfoStage, ...oldFlags } = stateData;
    let updatedModels: TModelsTypes[] = isForceUpdate
        ? models.filter(model => !currentlyRequestedModels.has(model) && clientInfoStage[model] !== EStage.loading)
        : models.filter(model => !currentlyRequestedModels.has(model) &&
            clientInfoStage[model] !== EStage.loading && clientInfoStage[model] !== EStage.loaded);

    // артефакт - проверка по старым флагам
    updatedModels = modelUpdatedByOldFlags(updatedModels, oldFlags, isForceUpdate);

    return updatedModels;
};

const setStatusForModels = (models: TModelsTypes[], stageForUpdate: EStage) =>
    (dispatch: TAppThunkDispatch) => {
        const isLoading = stageForUpdate == EStage.loading;

        models.forEach(model =>
            isLoading ? currentlyRequestedModels.add(model) : currentlyRequestedModels.delete(model));

        // старые статусы
        if (models.includes('products')) {
            dispatch(clientProductsLoading(isLoading));// артефакт
        }
        if (models.includes('contacts')) {
            dispatch(clientContactsLoading(isLoading));// артефакт
        }
        if (difference(['personal', 'payment', 'products', 'contacts'], models).length === 0) {
            dispatch(setFetchClientPayment(isLoading)); // артефакт
        }

        // актуальный статус
        dispatch(setClientInfoStage(getStageForModels(stageForUpdate, models)));
    };

/**
 * Получаем данные для авторизованного пользователя
 * isForceUpdate: принудительное обновление, не зависимо от наличия данных
 */
export const getInfoAll = (models: TModelsTypes[], isForceUpdate?: boolean) => {
    return async(dispatch: TAppThunkDispatch, getState: TGetState) => {
        const state = getState();
        const providerId = selectProviderId(state);
        const cookieAccessToken = getAccessToken();
        const hasAuthorization = selectHasAuthorization(state);

        // не выполняем запрос, если нет авторизации и провайдера
        if (!providerId || !(hasAuthorization || !!cookieAccessToken)) {
            return;
        }

        const {
            accessToken,
            clientInfoStage,

            // старые флаги - артефакты
            clientPaymentLoaded,
            clientPaymentFetching,
            clientPersonal,
            isClientProductsLoading,
            isClientContactsLoading,
        } = state.auth;

        const updatedModels = getFilteredModels(models, {
            clientInfoStage,
            clientPaymentFetching,
            isClientProductsLoading,
            isClientContactsLoading,
            clientPaymentLoaded,
            clientPersonal,
        }, isForceUpdate);

        // Не выполняем запрос если данные были загружены ранее или уже загружаются
        if (!updatedModels.length) {
            return;
        }

        const agreementNumber = getAgreementNumber();

        dispatch(setStatusForModels(updatedModels, EStage.loading));

        const resp = await doRequest.apiProfile<ResponseContactDto>(
            getInfoAllRequest(
                providerId,
                accessToken.length ? accessToken : cookieAccessToken,
                agreementNumber,
                updatedModels,
            ),
        );

        if (resp.isSuccess) {
            const { personal, payment, products, contacts } = resp.payload;

            const clientContacts = contacts ? parseContacts(contacts) : null;

            batch(() => {
                (updatedModels.includes('personal') && personal) && dispatch(setClientPersonal(personal));
                (updatedModels.includes('payment') && payment) && dispatch(setClientPayment(payment));
                (updatedModels.includes('products') && products) && dispatch(setClientProducts(products));
                (updatedModels.includes('contacts') && clientContacts) && dispatch(setClientContacts(clientContacts));

                dispatch(setStatusForModels(updatedModels, EStage.loaded));
                dispatch(setLogin({ accessToken }));
            });

        } else if (resp.error.code.toString() === '401') {
            window.location.replace(`${USER_LOGOUT_ROUTE}?referrer=${location.href}`);
        } else {
            dispatch(setStatusForModels(updatedModels, EStage.error));
        }
    };
};
